import { Box, Checkbox, FormControlLabel, Typography } from '@mui/material';
import PropTypes from 'prop-types';

import colors from 'common/constants/colors';

/**
 * Builds the sentence under the checkbox for one of its four states.
 *
 * Both sides of the toggle are stated, because a caption that only described the default would
 * leave the riskier choice unexplained at the moment the user makes it.
 */
const buildConsequenceSentence = ({ many, excluded, canPrintAnyway, anyFixableHere }) => {
  const them = many ? 'them' : 'it';
  const stays = many ? 'They stay' : 'It stays';
  const prints = many ? 'They print' : 'It prints';
  const stayInQueue = `${stays} in the work queue unprinted.`;
  const needsDispensableLot = 'A label needs a lot that can actually be dispensed';

  if (!canPrintAnyway && anyFixableHere) {
    return `${stayInQueue} ${needsDispensableLot} — fix ${them} above to print ${them}.`;
  }
  if (!canPrintAnyway) {
    return `${stayInQueue} ${needsDispensableLot}, and an inventory user has to add the product or its stock first.`;
  }
  if (excluded) return `${stayInQueue} Fix ${them} above, or untick to print ${them} anyway.`;

  return `${prints} with the current lot, which may not match what is dispensed. Fix ${them} above, or tick to leave ${them} out.`;
};

/**
 * POM-6010 — the toggle for items whose lot cannot be trusted.
 *
 * These rows are dropped from the batch by DEFAULT, which is why this arrives ticked. Their
 * reserved lot has expired or gone, so the label would carry a lot that may not match what
 * fulfilment dispenses — and the safe reading of that is not to print it. They stay in the work
 * queue unprinted and every other label prints on its own. Unticking is the deliberate opt-out.
 *
 * POM-6534: rendered inside the print bar's count block, so it carries only the checkbox and its
 * caption; the count line above it says how many labels print.
 */
const BulkLotNdcExcludedPanel = ({
  blockedCount,
  blockedStatusSummary,
  excluded,
  canPrintAnyway,
  anyFixableHere,
  onToggle,
}) => {
  const many = blockedCount > 1;

  return (
    <Box>
      {/* POM-6121 — a statement, not a choice, when there is no way back in. The checkbox is
          kept and locked rather than dropped, so the panel reads the same either way. */}
      <FormControlLabel
        checked={excluded}
        onChange={onToggle}
        disabled={!canPrintAnyway}
        control={<Checkbox size='small' />}
        label={
          <Typography variant='body2' sx={{ fontWeight: 600, color: colors.warningAmberText }}>
            {`Leave ${blockedCount} item${many ? 's' : ''} out of this batch`}
          </Typography>
        }
      />
      <Typography variant='caption' color='text.secondary' component='div' sx={{ ml: 3.75 }}>
        <Box component='span' sx={{ fontWeight: 600, color: colors.warningAmberText }}>
          {`${blockedStatusSummary}.`}
        </Box>{' '}
        {buildConsequenceSentence({ many, excluded, canPrintAnyway, anyFixableHere })}
      </Typography>
    </Box>
  );
};

BulkLotNdcExcludedPanel.propTypes = {
  blockedCount: PropTypes.number.isRequired,
  /** The held-back rows counted in the grid's chip words, e.g. "2 Expired lot · 1 No NDC". */
  blockedStatusSummary: PropTypes.string.isRequired,
  excluded: PropTypes.bool.isRequired,
  /** False when the exclusion cannot be undone — the control then reads as a statement. */
  canPrintAnyway: PropTypes.bool,
  /** False when no held-back row has a pencil — "fix them above" would then be a dead end. */
  anyFixableHere: PropTypes.bool,
  onToggle: PropTypes.func.isRequired,
};

BulkLotNdcExcludedPanel.defaultProps = {
  canPrintAnyway: false,
  anyFixableHere: true,
};

export default BulkLotNdcExcludedPanel;
